import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Divider, Stack, Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';

const CARBON_PRICE_USD = 25;
const ILS_PER_USD = 3.67;

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

export default function MeasureHomePage() {
  const { selectedProject, selectedSite, setSelectedSite } = useProject();
  const [loading, setLoading] = useState(false);
  const [siteData, setSiteData] = useState(null);
  const [aggregate, setAggregate] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedSite?.id) {
      // Per-site view — pull both the bundled site data and the timeseries
      setAggregate(null); setTimeseries(null);
      setLoading(true); setError('');
      Promise.all([
        fetch(`/v1/flux/sites/${selectedSite.id}`, { headers: bearerHeaders() }).then(r => r.json()),
        fetch(`/v1/flux/sites/${selectedSite.id}/timeseries?range=90d`, { headers: bearerHeaders() }).then(r => r.json()),
      ])
        .then(([j, ts]) => {
          if (!j.ok) throw new Error(j.message || 'fetch failed');
          setSiteData(j);
          if (ts.ok) setTimeseries(ts);
        })
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    } else if (selectedProject?.id) {
      // Aggregate view across all sites in the project
      setSiteData(null);
      setLoading(true); setError('');
      fetch(`/v1/flux/projects/${selectedProject.id}/aggregate`, { headers: bearerHeaders() })
        .then(r => r.json())
        .then(j => {
          if (!j.ok) throw new Error(j.message || 'fetch failed');
          setAggregate(j);
        })
        .catch(e => setError(e.message))
        .finally(() => setLoading(false));
    } else {
      setSiteData(null); setAggregate(null);
    }
  }, [selectedSite?.id, selectedProject?.id]);

  if (!selectedProject) return <EmptyState message="Pick a project from the header to begin." />;
  if (loading) return <Container maxWidth="lg"><Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box></Container>;
  if (error) return <Container maxWidth="lg"><Alert severity="error">{error}</Alert></Container>;

  if (!selectedSite && aggregate) {
    return <AggregateView project={selectedProject} aggregate={aggregate} onPickSite={setSelectedSite} />;
  }
  if (!siteData) return <EmptyState message={`Pick a site under ${selectedProject.name} to see carbon estimates.`} />;

  const screening = siteData?.screening?.[0] || null;
  const modelRun  = siteData?.modelRuns?.[0] || null;
  const strataCount = siteData?.strata?.length || 0;
  const sensorCount = siteData?.sensors?.length || 0;
  const calibrationCount = siteData?.calibrations?.length || 0;

  // Confidence ladder
  let level = 0;
  if (screening) level = 1;
  if (sensorCount > 0) level = 2;
  if (modelRun) level = 3;

  const totalTons = modelRun?.total_nee_tco2_yr ?? screening?.total_tco2_yr ?? null;
  const uncertaintyPct = modelRun?.uncertainty_pct ?? screening?.uncertainty_pct ?? null;
  const totalUSD = totalTons != null ? totalTons * CARBON_PRICE_USD : null;
  const totalILS = totalUSD != null ? totalUSD * ILS_PER_USD : null;

  // Cumulative stock: sum over strata of (years since plant × annual rate × area × survival).
  // Annual rate inferred from the latest screening's per_stratum (if available), else fallback rate.
  const screeningResults = screening?.results_json
    ? (typeof screening.results_json === 'string' ? JSON.parse(screening.results_json) : screening.results_json)
    : null;
  const thisYear = new Date().getFullYear();
  const totalAreaHa = (siteData?.strata || []).reduce((a, s) => a + (s.area_ha || 0), 0);
  let cumulativeStock = 0;
  for (const stratum of siteData?.strata || []) {
    const age = stratum.planted_year ? thisYear - stratum.planted_year : 10;
    const rate = screeningResults?.per_stratum?.find(p => p.stratum_id === stratum.id)?.rate_tco2_ha_yr ?? 4.0;
    cumulativeStock += age * rate * (stratum.area_ha || 0) * ((stratum.survival_pct || 85) / 100);
  }
  cumulativeStock = Math.round(cumulativeStock);

  // Trend over historical model runs (if multiple)
  const runs = siteData?.modelRuns || [];
  const trendDelta = runs.length >= 2
    ? +(((runs[0].total_nee_tco2_yr - runs[runs.length - 1].total_nee_tco2_yr) / runs[runs.length - 1].total_nee_tco2_yr) * 100).toFixed(1)
    : null;

  return (
    <>
      <Helmet><title>Dashboard | Measure</title></Helmet>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">{selectedProject.name}</Typography>
              <Typography variant="h4">{selectedSite.name}</Typography>
            </Box>
            <ConfidenceBadge level={level} />
          </Box>

          {/* Headline numbers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <MetricCard
              title="Annual sequestration"
              value={totalTons != null ? `${totalTons.toFixed(0)} tCO₂/yr` : '—'}
              caption={uncertaintyPct != null ? `±${uncertaintyPct}% uncertainty` : 'No estimate yet'}
            />
            <MetricCard
              title="Voluntary value"
              value={totalUSD != null ? `$${Math.round(totalUSD).toLocaleString()}/yr` : '—'}
              caption={totalILS != null ? `≈ ₪${Math.round(totalILS).toLocaleString()}/yr @ $25/tCO₂` : '—'}
            />
            <MetricCard
              title="Estimate range"
              value={
                totalTons != null && uncertaintyPct != null
                  ? `${Math.round(totalTons * (1 - uncertaintyPct / 100))} – ${Math.round(totalTons * (1 + uncertaintyPct / 100))}`
                  : '—'
              }
              caption="tCO₂/yr (lower / upper)"
            />
          </Box>

          {/* Carbon stocks breakdown — sourced from field measurements */}
          {timeseries?.stocks && timeseries.stocks.total_tCO2 > 0 && (
            <Box sx={{ p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" color="text.secondary">Carbon stock breakdown</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mt: 1 }}>
                <DashStock label="Above-ground" value={timeseries.stocks.agb_tCO2} unit="tCO₂" />
                <DashStock label="Below-ground" value={timeseries.stocks.bgb_tCO2} unit="tCO₂" />
                <DashStock label="Soil" value={timeseries.stocks.soc_tCO2} unit="tCO₂" />
                <DashStock label="Total" value={timeseries.stocks.total_tCO2} unit="tCO₂" highlight />
              </Box>
            </Box>
          )}

          {/* Compact 90-day flux trend */}
          {timeseries?.points?.length > 0 && (
            <Box sx={{ p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="overline" color="text.secondary">Net carbon uptake (NEE) · last 90 days</Typography>
                <Button component={RouterLink} to="/measure/monitoring" size="small" endIcon={<Iconify icon="eva:arrow-ios-forward-fill" />}>
                  Open Field Data
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {timeseries.last7Avg.nee.toFixed(2)} <Typography component="span" variant="caption" color="text.secondary">g C/m²/day</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    7-day avg · {timeseries.trend.nee_pct >= 0 ? '+' : ''}{timeseries.trend.nee_pct}% vs prior 23 days
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 280 }}>
                  <DashSparkline points={timeseries.points} />
                </Box>
              </Box>
            </Box>
          )}

          {/* Other site context */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <MetricCard
              title="Sensor coverage"
              value={`${sensorCount} bound`}
              caption={`${(siteData?.sensors || []).filter(s => s.type === 'sequestometer').length} SequestOmeter · ${(siteData?.sensors || []).filter(s => s.type === 'ec_tower').length} EC`}
            />
            <MetricCard
              title="Strata × area"
              value={`${strataCount} × ${totalAreaHa.toFixed(0)} ha`}
              caption="Site stratification"
            />
            <MetricCard
              title="Trend (model runs)"
              value={trendDelta != null ? `${trendDelta >= 0 ? '+' : ''}${trendDelta}%` : '—'}
              caption={trendDelta != null ? `across ${runs.length} historical runs` : 'Need 2+ runs to compute'}
            />
          </Box>

          {/* Data sources driving the estimate */}
          <Box sx={{ p: 3, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="overline" color="text.secondary">Data driving this estimate</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <SourceLine
                ok={!!screening}
                label="Stage 1 · Baseline (IPCC Tier 1)"
                detail={screening
                  ? `${strataCount} strata, ±${screening.uncertainty_pct ?? 50}% — last run ${formatDate(screening.created_at)}`
                  : 'Not run yet — baseline gives Tier 1 estimate (±50–100%)'}
                action={!screening && { to: '/measure/baseline', label: 'Run Baseline' }}
              />
              <SourceLine
                ok={sensorCount > 0}
                label="Stage 2 · Monitoring (live sensors + field data)"
                detail={sensorCount > 0
                  ? `${sensorCount} sensor${sensorCount === 1 ? '' : 's'} bound`
                  : 'No sensors bound yet — adds direct measurement and tightens uncertainty'}
                action={sensorCount === 0 && { to: '/measure/monitoring', label: 'Add sensors' }}
              />
              <SourceLine
                ok={!!modelRun}
                label="Stage 3 · Spatial Model (Haifa harmonized 10×10 m)"
                detail={modelRun
                  ? `Run ${formatDate(modelRun.created_at)} — version ${modelRun.model_version || 'unknown'}`
                  : 'No spatial model run yet — produces a per-pixel carbon map'}
                action={!modelRun && { to: '/measure/spatial', label: 'Run model' }}
              />
              <SourceLine
                ok={calibrationCount > 0}
                label="K calibration"
                detail={calibrationCount > 0
                  ? `${calibrationCount} campaign${calibrationCount === 1 ? '' : 's'} on record`
                  : 'No calibration recorded — K coefficient drives flux accuracy'}
                action={calibrationCount === 0 && { to: '/measure/calibrations', label: 'Record calibration' }}
              />
            </Stack>
          </Box>

          {/* Improve confidence prompt */}
          {level < 3 && (
            <Alert severity="info" icon={<Iconify icon="eva:trending-up-fill" />}>
              <Typography variant="body2">
                Confidence is currently <b>Stage {level || 0}</b>. Add the next data source to tighten the estimate from
                {' '}±{uncertaintyPct ?? 'unknown'}% toward ±15–30% (a model B improvement of 3–5×).
              </Typography>
            </Alert>
          )}
        </Stack>
      </Container>
    </>
  );
}

function AggregateView({ project, aggregate, onPickSite }) {
  const totalUSD = aggregate.total_tco2_yr * CARBON_PRICE_USD;
  const totalILS = totalUSD * ILS_PER_USD;
  const lo = aggregate.uncertainty_pct != null
    ? Math.round(aggregate.total_tco2_yr * (1 - aggregate.uncertainty_pct / 100))
    : null;
  const hi = aggregate.uncertainty_pct != null
    ? Math.round(aggregate.total_tco2_yr * (1 + aggregate.uncertainty_pct / 100))
    : null;

  return (
    <>
      <Helmet><title>Dashboard | Measure</title></Helmet>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="overline" color="text.secondary">{project.name}</Typography>
              <Typography variant="h4">All sites</Typography>
              <Typography variant="caption" color="text.secondary">
                {aggregate.site_count} site{aggregate.site_count === 1 ? '' : 's'} · {aggregate.total_area_ha.toFixed(1)} ha · {aggregate.total_strata} strata
              </Typography>
            </Box>
            <ConfidenceBadge level={aggregate.confidence_level} />
          </Box>

          {/* Rollup metrics */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <MetricCard
              title="Total annual sequestration"
              value={`${Math.round(aggregate.total_tco2_yr).toLocaleString()} tCO₂/yr`}
              caption={aggregate.uncertainty_pct != null ? `±${aggregate.uncertainty_pct}% (weighted)` : 'No estimate yet'}
            />
            <MetricCard
              title="Total voluntary value"
              value={`$${Math.round(totalUSD).toLocaleString()}/yr`}
              caption={`≈ ₪${Math.round(totalILS).toLocaleString()}/yr @ $25/tCO₂`}
            />
            <MetricCard
              title="Range across project"
              value={lo != null ? `${lo.toLocaleString()} – ${hi.toLocaleString()}` : '—'}
              caption="tCO₂/yr (lower / upper)"
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <MetricCard
              title="Total area"
              value={`${aggregate.total_area_ha.toFixed(1)} ha`}
              caption={`across ${aggregate.site_count} site${aggregate.site_count === 1 ? '' : 's'}`}
            />
            <MetricCard
              title="Sensor coverage"
              value={`${aggregate.total_sensors} bound`}
              caption={`${aggregate.total_sequestometer} SequestOmeter · ${aggregate.total_ec} EC`}
            />
            <MetricCard
              title="Aggregate confidence"
              value={['No data', 'Baseline only', 'Monitoring active', 'Full MRV'][aggregate.confidence_level]}
              caption="Limited by the weakest site"
            />
          </Box>

          {/* Per-site breakdown */}
          <Box>
            <Typography variant="h6" sx={{ mb: 1.5 }}>Sites in this project</Typography>
            <Stack spacing={1.5}>
              {aggregate.sites.map(s => <SiteRow key={s.site.id} entry={s} onClick={() => onPickSite(s.site)} />)}
            </Stack>
          </Box>

          {aggregate.confidence_level < 3 && (
            <Alert severity="info" icon={<Iconify icon="eva:trending-up-fill" />}>
              Aggregate confidence is currently <b>Stage {aggregate.confidence_level}</b>. The chain is only as strong as
              the weakest site — open any site below to see what's missing.
            </Alert>
          )}
        </Stack>
      </Container>
    </>
  );
}

function SiteRow({ entry, onClick }) {
  const tco2 = entry.latest_model_run?.total_nee_tco2_yr ?? entry.latest_screening?.total_tco2_yr ?? null;
  const unc = entry.latest_model_run?.uncertainty_pct ?? entry.latest_screening?.uncertainty_pct ?? null;
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
        cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h6">{entry.site.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.area_ha.toFixed(1)} ha · {entry.strata_count} strata · {entry.sensor_count} sensors
            </Typography>
          </Box>
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 600 }}>
                {tco2 != null ? `${Math.round(tco2)} tCO₂/yr` : 'No estimate yet'}
              </Box>
              {unc != null && <Typography component="span" variant="caption" color="text.secondary"> · ±{unc}%</Typography>}
              {entry.latest_calibration?.k_coefficient != null && (
                <Typography component="span" variant="caption" color="text.secondary"> · K={entry.latest_calibration.k_coefficient.toFixed(2)}</Typography>
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={['No data', '★☆☆ Baseline', '★★☆ Monitoring', '★★★ Full MRV'][entry.confidence_level]}
            color={['default', 'warning', 'info', 'success'][entry.confidence_level]}
          />
          <Iconify icon="eva:arrow-ios-forward-fill" sx={{ color: 'text.secondary' }} />
        </Box>
      </Box>
    </Box>
  );
}

function DashStock({ label, value, unit, highlight }) {
  return (
    <Box sx={{
      p: 1.5, borderRadius: 1,
      bgcolor: highlight ? 'success.lighter' : 'transparent',
      border: highlight ? 'none' : '1px dashed',
      borderColor: 'divider',
    }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25, color: highlight ? 'success.dark' : 'text.primary' }}>
        {value != null ? value.toLocaleString() : '—'} <Typography component="span" variant="caption" color="text.secondary">{unit}</Typography>
      </Typography>
    </Box>
  );
}

function DashSparkline({ points }) {
  const series = [{ name: 'NEE', data: points.map(p => ({ x: p.date, y: p.nee_gCm2d })) }];
  const options = {
    chart: { type: 'area', sparkline: { enabled: true }, animations: { enabled: false } },
    stroke: { width: 2, curve: 'smooth' },
    colors: ['#2e7d32'],
    fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.0, stops: [0, 100] } },
    tooltip: { x: { format: 'd MMM' }, y: { formatter: v => `${v.toFixed(2)} g C/m²/day` } },
    xaxis: { type: 'datetime' },
  };
  return <ReactApexChart options={options} series={series} type="area" height={70} />;
}

function ConfidenceBadge({ level }) {
  const labels = ['No data', 'Baseline only', 'Monitoring active', 'Full MRV'];
  const colors = ['default', 'warning', 'info', 'success'];
  const stars = '★'.repeat(level) + '☆'.repeat(3 - level);
  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Confidence</Typography>
      <Chip label={`${stars}  ${labels[level] || labels[0]}`} color={colors[level]} size="small" sx={{ fontWeight: 600 }} />
    </Box>
  );
}

function MetricCard({ title, value, caption }) {
  return (
    <Box sx={{ p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" color="text.secondary">{title}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{caption}</Typography>
    </Box>
  );
}

function SourceLine({ ok, label, detail, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Iconify
        icon={ok ? 'eva:checkmark-circle-2-fill' : 'eva:radio-button-off-fill'}
        sx={{ fontSize: 22, color: ok ? 'success.main' : 'text.disabled' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </Box>
      {action && (
        <Button component={RouterLink} to={action.to} size="small" variant="outlined">{action.label}</Button>
      )}
    </Box>
  );
}

function EmptyState({ message }) {
  return (
    <Container maxWidth="md">
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Dashboard</Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Box>
    </Container>
  );
}

function formatDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}
